import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  UserCheck, Search, Phone, Mail, FileText, Download, ChevronDown, ChevronUp, User, Eye, Trash2,
  Award, Calendar, Briefcase, Camera, Building2, Upload, Send, X, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { openBlobInNewTab, base64ToBlob } from '../utils/fileUtils';
import RoleBadges from '../components/RoleBadges';

// Helpers d'affichage
const formatDateFr = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleDateString('fr-FR');
  } catch { return String(dateStr); }
};
const calcAge = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age;
};
const formatDateAvecAge = (dateStr) => {
  const f = formatDateFr(dateStr);
  if (!f) return '';
  const age = calcAge(dateStr);
  return age != null ? `${f} (${age} ans)` : f;
};
const isExpirationProche = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return false;
  const diffMs = d.getTime() - Date.now();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays < 180; // alerte rouge si moins de 6 mois (ou expirée)
};

const ListeTuteurs = ({ user }) => {
  const [tuteurs, setTuteurs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTuteur, setExpandedTuteur] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(null);
  const [uploadData, setUploadData] = useState({ titre: '', description: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [centreDocuments, setCentreDocuments] = useState({});
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadTuteurs();
  }, []);

  const loadTuteurs = async () => {
    try {
      const response = await axios.get('/tuteurs');
      // Afficher immédiatement sans enrichissement lourd
      setTuteurs((response.data || []).map(t => ({ ...t, documents: [] })));
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Charger profil + documents uniquement quand on déplie un tuteur
  const loadTuteurDetails = async (userId) => {
    try {
      const [profileRes, docsRes] = await Promise.all([
        axios.get(`/tuteurs/${userId}`).catch(() => ({ data: {} })),
        axios.get(`/documents/${userId}`).catch(() => ({ data: [] }))
      ]);
      setTuteurs(prev => prev.map(t => {
        if (t.user_id === userId) {
          return { ...t, ...(profileRes.data?.profile || {}), documents: docsRes.data || [], _loaded: true };
        }
        return t;
      }));
    } catch (e) {
      // Silently fail - data from /tuteurs is already displayed
    }
  };

  // Voir un document (ouvrir dans un nouvel onglet)
  const loadCentreDocuments = async (tuteurId) => {
    try {
      const response = await axios.get(`/documents-tuteur/${tuteurId}`);
      setCentreDocuments(prev => ({ ...prev, [tuteurId]: response.data || [] }));
    } catch (error) {
      setCentreDocuments(prev => ({ ...prev, [tuteurId]: [] }));
    }
  };

  const handleUploadToTuteur = async (tuteurId) => {
    if (!uploadData.titre.trim()) { toast.error('Le titre est requis'); return; }
    setUploading(true);
    const formData = new FormData();
    if (selectedFile) formData.append('file', selectedFile);
    formData.append('destinataire_id', tuteurId);
    formData.append('titre', uploadData.titre);
    formData.append('description', uploadData.description || '');
    try {
      await axios.post('/documents-tuteur/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(selectedFile ? 'Document envoyé au tuteur' : 'Message envoyé au tuteur');
      setShowUploadModal(null); setSelectedFile(null); setUploadData({ titre: '', description: '' });
      loadCentreDocuments(tuteurId);
    } catch (error) { toast.error('Erreur lors de l\'envoi'); }
    finally { setUploading(false); }
  };

  const viewCentreDoc = async (doc) => {
    try {
      const response = await axios.get(`/documents-tuteur/${doc.document_id}/download`, { responseType: 'blob' });
      openBlobInNewTab(new Blob([response.data], { type: doc.content_type || 'application/pdf' }));
    } catch (error) { toast.error('Erreur'); }
  };

  const downloadCentreDoc = async (doc) => {
    try {
      const response = await axios.get(`/documents-tuteur/${doc.document_id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', doc.filename); document.body.appendChild(link); link.click(); link.remove();
    } catch (error) { toast.error('Erreur'); }
  };

  const deleteCentreDoc = async (documentId, tuteurId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`/documents-tuteur/${documentId}`);
      toast.success('Document supprimé'); loadCentreDocuments(tuteurId);
    } catch (error) { toast.error('Erreur'); }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  const viewDocument = async (documentId) => {
    try {
      toast.info('Chargement du document...');
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const blob = base64ToBlob(doc.file_data, doc.content_type || 'application/pdf');
        openBlobInNewTab(blob);
      } else {
        toast.error('Document non disponible');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ouverture du document');
    }
  };

  // Télécharger un document
  const downloadDocument = async (documentId, filename) => {
    try {
      toast.info('Téléchargement...');
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const byteCharacters = atob(doc.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: doc.content_type || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || doc.filename || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Document téléchargé');
      }
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Supprimer un document
  const deleteDocument = async (documentId, tuteurId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ? Cette action supprimera également le document de l\'espace personnel du tuteur.')) {
      return;
    }
    try {
      await axios.delete(`/documents/${documentId}`);
      toast.success('Document supprimé');
      setTuteurs(prev => prev.map(t => {
        if (t.user_id === tuteurId) {
          return { ...t, documents: t.documents.filter(d => d.document_id !== documentId) };
        }
        return t;
      }));
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Supprimer un tuteur
  const deleteTuteur = async (tuteurId, tuteurNom) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le tuteur ${tuteurNom} ? Cette action est irréversible.`)) {
      return;
    }
    try {
      await axios.delete(`/tuteurs/${tuteurId}`);
      toast.success('Tuteur supprimé');
      setTuteurs(prev => prev.filter(t => t.user_id !== tuteurId));
    } catch (error) {
      toast.error('Erreur lors de la suppression du tuteur');
    }
  };

  const filteredTuteurs = tuteurs.filter(t => {
    const nom = t.user_info?.nom || t.nom || '';
    const prenom = t.user_info?.prenom || t.prenom || '';
    const email = t.user_info?.email || '';
    return `${nom} ${prenom} ${email}`.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-display font-bold text-[#0F172A]">Tuteurs</h1>
        <p className="text-slate-600">Profils complets et documents des tuteurs</p>
      </div>

      {/* Recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="text"
          placeholder="Rechercher un tuteur..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
          data-testid="search-tuteur"
        />
      </div>

      {/* Stats */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center gap-4">
        <UserCheck className="h-8 w-8 text-purple-600" />
        <div>
          <p className="text-2xl font-bold text-purple-700">{tuteurs.length}</p>
          <p className="text-sm text-purple-600">Tuteurs enregistrés</p>
        </div>
      </div>

      {/* Liste */}
      <div className="space-y-4">
        {filteredTuteurs.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
            <UserCheck className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucun tuteur trouvé</p>
          </div>
        ) : (
          filteredTuteurs.map((tuteur) => {
            const isExpanded = expandedTuteur === tuteur.user_id;
            const nom = tuteur.user_info?.nom || tuteur.nom || '';
            const prenom = tuteur.user_info?.prenom || tuteur.prenom || '';
            const fullName = `${prenom} ${nom}`.trim() || 'Tuteur';
            const diplomes = tuteur.documents?.filter(d => (d.document_type || d.type || '').toLowerCase().includes('diplome')) || [];
            const autresDocs = tuteur.documents?.filter(d => !(d.document_type || d.type || '').toLowerCase().includes('diplome')) || [];
            
            return (
              <div
                key={tuteur.user_id}
                className="bg-white border border-slate-200 rounded-xl hover:shadow-lg transition-all"
                data-testid={`tuteur-${tuteur.user_id}`}
              >
                {/* En-tête */}
                <div 
                  className="p-4 cursor-pointer hover:bg-slate-50"
                  onClick={() => {
                    const newId = isExpanded ? null : tuteur.user_id;
                    setExpandedTuteur(newId);
                    if (newId) {
                      if (!tuteur._loaded) loadTuteurDetails(newId);
                      if (!centreDocuments[newId]) loadCentreDocuments(newId);
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Photo */}
                      <div className="w-14 h-18 rounded-md overflow-hidden bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white text-xl font-bold">
                        {(tuteur.photo || tuteur.photo_identite || tuteur.user_info?.photo_identite || tuteur.user_info?.photo_url) ? (
                          <img src={tuteur.photo || tuteur.photo_identite || tuteur.user_info?.photo_identite || tuteur.user_info?.photo_url} alt={fullName} className="w-full h-full object-cover" />
                        ) : (
                          fullName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-[#0F172A]">
                          {tuteur.genre || ''} {fullName}
                        </h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                          {(tuteur.user_info?.email || tuteur.email) && (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" /> {tuteur.user_info?.email || tuteur.email}
                            </span>
                          )}
                          {tuteur.telephone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" /> {tuteur.telephone}
                            </span>
                          )}
                        </div>
                        {user?.role === 'centre' && (
                          <RoleBadges userId={tuteur.user_id} roles={tuteur.user_info?.roles || tuteur.roles || ['tuteur']} onRolesChange={(newRoles) => {
                            setTuteurs(prev => prev.map(t => t.user_id === tuteur.user_id ? {...t, roles: newRoles, user_info: {...t.user_info, roles: newRoles}} : t));
                          }} />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                        {diplomes.length} diplôme(s)
                      </span>
                      <span className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
                        {autresDocs.length} doc(s)
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteTuteur(tuteur.user_id, fullName);
                        }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer le tuteur"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Détails dépliés */}
                {isExpanded && (
                  <div className="border-t border-slate-200 bg-gradient-to-b from-purple-50 to-white p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      
                      {/* Colonne 1: Profil complet */}
                      <div className="bg-white p-5 rounded-xl border border-purple-200 shadow-sm">
                        <h4 className="font-semibold text-purple-800 mb-4 flex items-center gap-2">
                          <User className="h-5 w-5" />
                          Profil Complet
                        </h4>
                        
                        {/* Photo grande */}
                        <div className="flex justify-center mb-4">
                          <div className="w-20 h-28 rounded-md overflow-hidden bg-purple-100 border-4 border-purple-200">
                            {(tuteur.photo || tuteur.photo_identite || tuteur.user_info?.photo_identite || tuteur.user_info?.photo_url) ? (
                              <img src={tuteur.photo || tuteur.photo_identite || tuteur.user_info?.photo_identite || tuteur.user_info?.photo_url} alt={fullName} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-purple-400">
                                <Camera className="h-8 w-8" />
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Genre</span>
                            <span className="font-medium">{tuteur.genre || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Nom</span>
                            <span className="font-medium">{nom || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Prénom</span>
                            <span className="font-medium">{prenom || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Date de naissance</span>
                            <span className="font-medium">{formatDateAvecAge(tuteur.date_naissance) || (tuteur.age ? `${tuteur.age} ans` : '-')}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Téléphone</span>
                            <span className="font-medium">{tuteur.telephone || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Email</span>
                            <span className="font-medium text-xs">{tuteur.user_info?.email || tuteur.email || '-'}</span>
                          </div>
                          {(tuteur.adresse || tuteur.code_postal || tuteur.ville) && (
                            <div className="py-2 border-b border-slate-100">
                              <span className="text-slate-500 block mb-1">Adresse</span>
                              <span className="font-medium text-xs block">
                                {tuteur.adresse || ''}
                                {(tuteur.code_postal || tuteur.ville) && (
                                  <><br/>{tuteur.code_postal || ''} {tuteur.ville || ''}</>
                                )}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Diplôme principal</span>
                            <span className="font-medium">{tuteur.diplome || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Date d'obtention</span>
                            <span className="font-medium">{formatDateFr(tuteur.date_diplome) || tuteur.annee_diplome || tuteur.date_obtention || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Années d'expérience</span>
                            <span className="font-medium">{tuteur.experience_annees ? `${tuteur.experience_annees} an${tuteur.experience_annees > 1 ? 's' : ''}` : '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">N° carte pro</span>
                            <span className="font-medium text-xs">{tuteur.carte_pro_numero || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Expiration carte pro</span>
                            <span className={`font-medium ${isExpirationProche(tuteur.carte_pro_expiration) ? 'text-red-600' : ''}`}>
                              {formatDateFr(tuteur.carte_pro_expiration) || '-'}
                            </span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Entreprise</span>
                            <span className="font-medium">{tuteur.entreprise || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Lien professionnel</span>
                            <span className="font-medium capitalize">{tuteur.lien_professionnel || '-'}</span>
                          </div>
                          {tuteur.lien_professionnel_autre && (
                            <div className="flex justify-between py-2 border-b border-slate-100">
                              <span className="text-slate-500">Autre lien</span>
                              <span className="font-medium">{tuteur.lien_professionnel_autre}</span>
                            </div>
                          )}
                        </div>

                        {/* Signature */}
                        {tuteur.signature && (
                          <div className="mt-4 pt-4 border-t border-slate-200">
                            <p className="text-sm text-slate-500 mb-2">Signature</p>
                            <img src={tuteur.signature} alt="Signature" className="max-h-16 border border-slate-200 rounded" />
                          </div>
                        )}
                      </div>

                      {/* Colonne 2: Diplômes */}
                      <div className="bg-white p-5 rounded-xl border border-purple-200 shadow-sm">
                        <h4 className="font-semibold text-purple-800 mb-4 flex items-center gap-2">
                          <Award className="h-5 w-5" />
                          Diplômes ({diplomes.length})
                        </h4>
                        {diplomes.length === 0 ? (
                          <div className="text-center py-8 bg-slate-50 rounded-lg">
                            <Award className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                            <p className="text-slate-500 text-sm">Aucun diplôme uploadé</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {diplomes.map((doc, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100">
                                <div className="flex items-center gap-2">
                                  <Award className="h-4 w-4 text-purple-600" />
                                  <span className="text-sm font-medium text-purple-800 truncate max-w-[150px]">
                                    {doc.document_type || doc.type || 'Diplôme'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => viewDocument(doc.document_id)}
                                    className="p-2 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors"
                                    title="Voir"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => downloadDocument(doc.document_id, `${doc.document_type || 'diplome'}_${fullName}`)}
                                    className="p-2 text-green-500 hover:bg-green-100 rounded-lg transition-colors"
                                    title="Télécharger"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => deleteDocument(doc.document_id, tuteur.user_id)}
                                    className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Colonne 3: Autres documents */}
                      <div className="bg-white p-5 rounded-xl border border-purple-200 shadow-sm">
                        <h4 className="font-semibold text-purple-800 mb-4 flex items-center gap-2">
                          <FileText className="h-5 w-5" />
                          Autres Documents ({autresDocs.length})
                        </h4>
                        {autresDocs.length === 0 ? (
                          <div className="text-center py-8 bg-slate-50 rounded-lg">
                            <FileText className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                            <p className="text-slate-500 text-sm">Aucun autre document</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {autresDocs.map((doc, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-slate-600" />
                                  <span className="text-sm font-medium text-slate-700 truncate max-w-[150px]">
                                    {doc.document_type || doc.type || 'Document'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => viewDocument(doc.document_id)}
                                    className="p-2 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors"
                                    title="Voir"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => downloadDocument(doc.document_id, `${doc.document_type || 'doc'}_${fullName}`)}
                                    className="p-2 text-green-500 hover:bg-green-100 rounded-lg transition-colors"
                                    title="Télécharger"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => deleteDocument(doc.document_id, tuteur.user_id)}
                                    className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Section Documents envoyés par le centre */}
                    <div className="mt-4 bg-white p-3 sm:p-5 rounded-xl border border-[#8DC63F]/30 shadow-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                        <h4 className="font-semibold text-[#0F172A] text-sm flex items-center gap-2">
                          <Send className="h-4 w-4 text-[#8DC63F] flex-shrink-0" />
                          <span className="truncate">Docs centre ({(centreDocuments[tuteur.user_id] || []).length})</span>
                        </h4>
                        <Button size="sm" onClick={() => setShowUploadModal(tuteur.user_id)} className="bg-[#8DC63F] hover:bg-[#7db336] text-xs whitespace-nowrap">
                          <Upload className="h-3.5 w-3.5 mr-1" /> Envoyer
                        </Button>
                      </div>
                      {(centreDocuments[tuteur.user_id] || []).length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4">Aucun document ou message envoyé</p>
                      ) : (
                        <div className="space-y-2">
                          {(centreDocuments[tuteur.user_id] || []).map(doc => (
                            <div key={doc.document_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                              <div className="flex items-center gap-3">
                                {doc.type === 'message' ? <MessageSquare className="h-4 w-4 text-blue-500" /> : <FileText className="h-4 w-4 text-[#8DC63F]" />}
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-[#0F172A]">{doc.titre}</span>
                                    {doc.type === 'message' && <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">Message</span>}
                                  </div>
                                  {doc.description && <p className="text-xs text-slate-600">{doc.description}</p>}
                                  <div className="flex items-center gap-2 text-xs text-slate-400">
                                    {doc.filename && <span>{doc.filename}</span>}
                                    {doc.file_size && <span>{formatFileSize(doc.file_size)}</span>}
                                    <span>{new Date(doc.created_at).toLocaleDateString('fr-FR')}</span>
                                    {doc.lu && <span className="text-green-600 font-medium">Lu</span>}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                {doc.type !== 'message' && (
                                  <>
                                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => viewCentreDoc(doc)}><Eye className="h-3 w-3" /></Button>
                                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => downloadCentreDoc(doc)}><Download className="h-3 w-3" /></Button>
                                  </>
                                )}
                                <Button variant="outline" size="sm" className="h-7 text-xs text-red-600 border-red-200" onClick={() => deleteCentreDoc(doc.document_id, tuteur.user_id)}><Trash2 className="h-3 w-3" /></Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Modal Upload Document / Message */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#0F172A]">Envoyer un document ou message au tuteur</h3>
              <button onClick={() => { setShowUploadModal(null); setSelectedFile(null); setUploadData({ titre: '', description: '' }); }} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Titre *</label>
                <input type="text" value={uploadData.titre} onChange={(e) => setUploadData({ ...uploadData, titre: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#8DC63F]" placeholder="Ex: Convention, Attestation..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Message / Description</label>
                <textarea value={uploadData.description} onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-lg resize-none" placeholder="Saisissez votre message ici..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fichier (optionnel)</label>
                <input type="file" ref={fileInputRef} onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} className="hidden" />
                <div onClick={() => fileInputRef.current?.click()} className="w-full border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-[#8DC63F]">
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileText className="h-5 w-5 text-[#8DC63F]" />
                      <span className="text-sm">{selectedFile.name}</span>
                      <button onClick={(e) => { e.stopPropagation(); setSelectedFile(null); if(fileInputRef.current) fileInputRef.current.value = ''; }} className="ml-1 text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <div><Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" /><p className="text-sm text-slate-500">Cliquez pour sélectionner (optionnel)</p></div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => { setShowUploadModal(null); setSelectedFile(null); }} className="flex-1">Annuler</Button>
              <Button onClick={() => handleUploadToTuteur(showUploadModal)} disabled={uploading || !uploadData.titre.trim()} className="flex-1 bg-[#8DC63F] hover:bg-[#7db336]" data-testid="send-doc-message-btn">
                {uploading ? 'Envoi...' : <><Send className="h-4 w-4 mr-2" /> Envoyer</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListeTuteurs;
